"use client"
import { useTranslations } from "next-intl"

import { Button } from "@/components/ui/button"
import { NumericInput } from "@/components/ui/numeric-input"
import { integerCodec } from "@/lib/forms/numeric-draft"
import { Label } from "@/components/ui/label"
import { DialogDescription } from "@/components/ui/dialog"
import { DialogCard } from "@/components/ui/dialog-card"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { DockSheet } from "@/components/ui/dock-sheet"
import { SHEET_Z_DEFAULT, SHEET_Z_GLOBAL } from "@/lib/sheet-styles"
import { DetailsTab, DetailsTabRef } from "./details-tab"
import { ConditionTab } from "./condition-tab"
import { EditShippingTab } from "./EditShippingTab"
import { createClient } from "@/utils/supabase/client"
import { useState, useEffect, useRef, useCallback, useMemo } from "react"
import { useMediaQuery } from "@/hooks/use-media-query"
import { useToast } from "@/hooks/use-toast"
import { getNormalizedImageFile, getSafeImageExtension } from "@/lib/image-upload"
import { storageBucket, StoragePath } from "@/lib/storage"
import { asRawImageRef, isPersistedImageRef, rawImageRefValue, rawImageRefValues, type RawImageRef } from "@/lib/storage/url"
import { DEFAULT_CURRENCY } from "@/lib/currencies"
import { creationFlowPrimaryCtaClass } from "@/lib/creation-flow-form-styles"
import type { ListingRow, ListingCategory, CardListingDraft, ComicDraft, ListingType } from "@/types/listing"
import { PreorderEditSection, usePreorderLockState } from "./preorder-edit-section"
import { computeNeedsGrading } from "@/lib/edit-listing-state"
import { updateListing, UpdateListingError } from "@/lib/listings/update-listing"
import { unreachableCategory, type PatchListingRequest } from "@kraahl/contract"
import type { ShippingModeAndParcelValue } from "@/components/shipping/ShippingModeToggleAndParcelForm"
import { useShippingOrigin } from "@/hooks/use-shipping-origin"

export type ShippingOption = {
  id: string
  name: string
  description: string
  icon: React.ElementType
  price: string
  bannedCountries: string[]
  allowCombined?: boolean
  combinedFee?: string
}

/**
 * `preview` is BOTH a display value and a persistence value — the save path
 * pushes it straight into `listings.image_urls` when there is no `file`
 * (see `handleSave`). It therefore stays RAW: a bucket-relative storage path,
 * a legacy absolute URL, or a client-only `blob:` preview of an unsent `File`.
 *
 * Branded `RawImageRef` so (a) a render site cannot pass it to `<img src>` /
 * `next/image` without `resolveStorageUrl`, and (b) a resolved absolute URL
 * cannot be assigned back in — that is exactly the regression that would undo
 * the phase-C migration on every listing edit.
 *
 * @see .tondocs/images/HANDOFF_RELATIVE_STORAGE_PATHS_2026_07_27.md §9
 */
export interface PhotoItem {
  file: File | null
  preview: RawImageRef
  photoId: string
}

type Listing = ListingRow

interface EditListingDialogProps {
  open: boolean
  onOpenChangeAction: (open: boolean) => void
  listing: Listing | null
  onSaveAction: (updatedListing?: Listing) => void
  hideQuantity?: boolean
  formData: {
    title: string
    description: string
    price: number
    shipping_config: any
    grade: number
    condition: string
    quantity: number
    images?: PhotoItem[]
    // Category fields
    brand_id?: string
    theme_id?: string
    product_type_id?: string
    theme_custom_text?: string
    brand_custom_text?: string
    product_type_custom_text?: string
    scheduled_publish_at?: string | null
    listingCategory?: ListingCategory
    cardDraft?: CardListingDraft
    comicDraft?: ComicDraft
    currency?: string | null
    listing_type?: ListingType
    listing_format?: string
    // Phase 3 (2026-06-29): `accepts_offers` collapsed into `listing_format`.
    // Derive via `isOfferEnabledFormat()` from `@kraahl/contract`.
    auto_decline_below?: number | null
    auction_start_price?: number
    auction_reserve_price?: number | null
    max_orders?: number | null
    // Shippo white-label (WS3.2 — edit-flow parity for live_rate).
    // Canonical-only (slice C2): grams + mm. Display ⇄ canonical conversion
    // lives in `ShippingModeToggleAndParcelForm`.
    shippingMode?: "legacy" | "live_rate"
    parcel?: {
      weight?: number
      length?: number
      width?: number
      height?: number
    }
    /**
     * Per-listing ship-from override pin per ADR_LISTING_SHIP_FROM_ADDRESS.md.
     * `undefined` (key absent on the form) ≡ resolution falls back to
     * seller's `is_main` row. A uuid ≡ the seller picked a specific
     * `address_book` row. Set via the EditShippingTab picker.
     */
    ship_from_address_id?: string | null
  }
  onFormChangeAction: (
    field: string,
    value: string | number | string[] | ShippingOption[] | any
  ) => void
  selectedShippingMethods: string[]
  customShippingOptions: ShippingOption[]
  isAdminContext?: boolean
  /**
   * Listing OWNER's `user_id`. Set by the admin editor so the shipping tab
   * loads (and writes) the viewed seller's methods rather than the logged-in
   * admin's. Undefined when a seller edits their own listing.
   */
  sellerUserId?: string
  elevatedForChat?: boolean
  disableSaveWhenUnchanged?: boolean
  hasFormChanges?: boolean
  loading?: boolean
}

const supabase = createClient()

// Upload images helper function - EXACTLY like creation-flow review-step.tsx
async function uploadImages(
  photoItems: PhotoItem[],
  title: string
): Promise<{ updatedPhotos: PhotoItem[]; publicUrls: RawImageRef[] }> {
  const updatedPhotos = [...photoItems]
  // PERSISTENCE array — every entry lands in `listings.image_urls`.
  const uploadedUrls: RawImageRef[] = []

  // Process each photo sequentially and ALWAYS push to uploadedUrls
  for (let i = 0; i < photoItems.length; i++) {
    const photo = photoItems[i]

    // PRIMARY CHECK: Has actual File object (new photo to upload)
    if (photo && photo.file instanceof File) {
      // NEW FILE: Normalize (convert HEIC to JPEG if needed) before upload
      let fileToUpload = photo.file
      try {
        const normalized = await getNormalizedImageFile(photo.file)
        fileToUpload = normalized.file // This will be JPEG if original was HEIC
      } catch (error) {
        console.error("❌ Error normalizing image file:", error)
        // Fallback to original file if normalization fails
      }

      // Get safe extension (HEIC files will return "jpg" after normalization)
      const extension = getSafeImageExtension(fileToUpload)
      const filePath = StoragePath.listing(title, i, extension)

      try {
        const { error: uploadError } = await storageBucket(supabase)
          .upload(filePath, fileToUpload, { upsert: true })

        if (uploadError) {
          console.error("❌ Error uploading photo:", uploadError)
          continue
        }

        // Persist the bucket-relative path (read funnels resolve it).
        // @see .tondocs/images/HANDOFF_RELATIVE_STORAGE_PATHS_2026_07_27.md §3 L4
        uploadedUrls.push(asRawImageRef(filePath)) // Push new path in correct position
        updatedPhotos[i] = {
          file: null,
          preview: asRawImageRef(filePath),
          photoId: photo.photoId || `uploaded-${Date.now()}-${i}`
        }
        
      } catch (error) {
        console.error("❌ Error in upload process:", error)
        continue
      }
      
    // SECONDARY CHECK: No File object, check if valid existing photo
    } else if (photo && !photo.file && photo.preview) {
      
      if (isPersistedImageRef(photo.preview)) {
        // EXISTING persisted ref: a legacy absolute URL *or* a bucket-relative
        // storage path (`listings/foo.jpg`) — keep as-is.
        //
        // This used to be `startsWith("http")`. Once the writers began
        // persisting paths (phase B) and the DB was migrated (phase C), every
        // existing photo failed BOTH branches, fell into the `else` below and
        // was `continue`d — i.e. saving any edit silently DROPPED all of the
        // listing's existing images. Same predicate bug the creation flow hit
        // (handoff §8 item 4); `isPersistedImageRef` is the shared fix.
        uploadedUrls.push(photo.preview)

      } else if (rawImageRefValue(photo.preview).startsWith("blob:")) {
        // BLOB URL but no file - this might be a processed existing photo
        console.warn(`⚠️ Found BLOB preview without File at index ${i} - likely corrupted photo, skipping:`, {
          photoId: photo.photoId,
          preview: rawImageRefValue(photo.preview).substring(0, 50)
        })
        continue
        
      } else {
        // OTHER preview type
        console.warn(`⚠️ Unknown preview type at index ${i}, skipping:`, {
          photoId: photo.photoId,
          preview: rawImageRefValue(photo.preview).substring(0, 50)
        })
        continue
      }
      
    } else if (typeof photo === "string") {
      // Direct URL string (fallback case)
      const photoStr = photo as string
      uploadedUrls.push(asRawImageRef(photoStr))
      
    } else {
      // INVALID: Skip this photo
      console.warn(`⚠️ Skipping completely invalid photo at index ${i}:`, {
        type: typeof photo,
        hasFile: photo && 'file' in photo,
        hasPreview: photo && 'preview' in photo,
        photoData: photo
      })
      continue
    }
  }
  
  return { updatedPhotos, publicUrls: uploadedUrls }
}

export function EditListingDialog({
  open,
  onOpenChangeAction,
  listing,
  onSaveAction,
  hideQuantity = false,
  formData,
  onFormChangeAction,
  selectedShippingMethods,
  customShippingOptions,
  isAdminContext = false,
  sellerUserId,
  elevatedForChat = false,
  disableSaveWhenUnchanged = false,
  hasFormChanges = true,
  loading: loadingProp = false,
}: EditListingDialogProps) {
  const tML = useTranslations("myListings");
  const tPreorderEdit = useTranslations("preorder.edit");
  const { toast } = useToast()
  const isDesktop = useMediaQuery("(min-width: 768px)")
  const [activeTab, setActiveTab] = useState("details")
  const [isLoading, setIsLoading] = useState(false)
  const detailsTabRef = useRef<DetailsTabRef>(null)
  const currencyCode = (listing as any)?.currency || DEFAULT_CURRENCY
  const isPreorderListing = (listing as { type?: string } | null)?.type === "preorder"
  const preorderLock = usePreorderLockState(listing?.id, isPreorderListing)
  const [preorderFields, setPreorderFields] = useState<{
    preorder_deadline: string | null
    estimated_delivery: string | null
    min_orders: number | null
    max_orders: number | null
    isValid: boolean
  }>({
    preorder_deadline: (listing as any)?.preorder_deadline ?? null,
    estimated_delivery: (listing as any)?.estimated_delivery ?? null,
    min_orders: (listing as any)?.min_orders ?? null,
    max_orders: (listing as any)?.max_orders ?? null,
    isValid: true,
  })

  // Pre-order fields (deadline/eta/min/max) are edited in PreorderEditSection,
  // which writes to local state instead of formData — so the parent's
  // hasFormChanges (formData snapshot) never sees them and the Save button
  // stays disabled. Track their own dirty state: the first emit (on mount) is
  // the loaded baseline, later emits are user edits.
  const preorderBaselineRef = useRef<string | null>(null)
  const preorderSignature = (f: {
    preorder_deadline: string | null
    estimated_delivery: string | null
    min_orders: number | null
    max_orders: number | null
  }) =>
    JSON.stringify([
      f.preorder_deadline ?? null,
      f.estimated_delivery ?? null,
      f.min_orders ?? null,
      f.max_orders ?? null,
    ])
  const handlePreorderChange = useCallback(
    (next: {
      preorder_deadline: string | null
      estimated_delivery: string | null
      min_orders: number | null
      max_orders: number | null
      isValid: boolean
    }) => {
      setPreorderFields(next)
      if (preorderBaselineRef.current === null) {
        preorderBaselineRef.current = preorderSignature(next)
      }
    },
    [],
  )
  const preorderDirty =
    isPreorderListing &&
    preorderBaselineRef.current !== null &&
    preorderSignature(preorderFields) !== preorderBaselineRef.current

  // Compute shipping props from formData.shipping_config to keep them in sync
  const currentShippingConfig = Array.isArray(formData.shipping_config) ? formData.shipping_config : []
  const computedSelectedMethods = currentShippingConfig
    .map((item: any) => item?.shipping_method_id)
    .filter((id: unknown): id is string => typeof id === "string" && id.length > 0)
  const computedCustomOptions = currentShippingConfig.length > 0 ? currentShippingConfig : customShippingOptions

  const effectiveNeedsGrading = useMemo(
    () => computeNeedsGrading(formData),
    [formData.listingCategory, formData.condition, formData.cardDraft],
  )

  // Whole-number fields (stock, preorder caps). Shared instance — the codec
  // is stateless, so one is enough for all three inputs.
  const countCodec = useMemo(() => integerCodec(), [])

  // WS3.2 — derive the controlled value the Primitive expects from the form
  // snapshot. Canonical-only (slice C2): grams + mm. Re-derived on every
  // render — the surface is small and cheap, so no useMemo.
  const shippingModeValue: ShippingModeAndParcelValue = {
    shippingMode: formData.shippingMode ?? "legacy",
    parcel: {
      weight: formData.parcel?.weight,
      length: formData.parcel?.length,
      width: formData.parcel?.width,
      height: formData.parcel?.height,
    },
  }
  const shippingModeLocked = Boolean(
    (listing as { shipping_mode_locked?: boolean } | null)?.shipping_mode_locked,
  )
  const handleShippingModeChange = useCallback(
    (next: ShippingModeAndParcelValue) => {
      onFormChangeAction("shippingMode", next.shippingMode)
      onFormChangeAction("parcel", next.parcel)
    },
    [onFormChangeAction],
  )

  // Per-listing ship-from pin (ADR_LISTING_SHIP_FROM_ADDRESS.md Phase 1).
  // The form starts with whatever the loaded listing carried; the picker
  // writes either a uuid (chose a non-main row) or `null` (chose main →
  // clear pin). Untouched edits keep `formData.ship_from_address_id`
  // strictly `undefined`, which the intent assembly omits from the PATCH
  // body so the column stays untouched.
  const initialShipFromAddressId =
    (listing as { ship_from_address_id?: string | null } | null)
      ?.ship_from_address_id ?? null
  const currentShipFromAddressId =
    formData.ship_from_address_id !== undefined
      ? formData.ship_from_address_id
      : initialShipFromAddressId
  const handleShipFromAddressChange = useCallback(
    (next: string | null) => {
      onFormChangeAction("ship_from_address_id", next)
    },
    [onFormChangeAction],
  )

  // Single resolver of "what country is this listing shipping from?" — feeds
  // both EditShippingTab callsites below (the normal Shipping tab AND the
  // preorder-limited panel). Before this hook, the normal tab forgot to
  // pass the origin entirely, so the currency-mismatch warning silently
  // disappeared and the address picker never rendered on edit.
  const { binding: shippingOriginBinding } = useShippingOrigin({
    shipFromAddressId: currentShipFromAddressId,
    onShipFromAddressChange: handleShipFromAddressChange,
  })

  useEffect(() => {
    if (!open) {
      setActiveTab("details")
    }
  }, [open])

  const isPreorderLimitedEditMode =
    isPreorderListing && preorderLock.locked && !preorderLock.loading
  const preorderEditLoading = isPreorderListing && preorderLock.loading

  useEffect(() => {
    if (open && isPreorderLimitedEditMode) {
      setActiveTab("shipping")
    }
  }, [open, isPreorderLimitedEditMode])

  useEffect(() => {
    if (open && !isDesktop) {
      document.body.style.overflow = "hidden"
      return () => { document.body.style.overflow = "" }
    }
  }, [open, isDesktop])

  // Stabilize the shipping update callback to prevent infinite loops
  const handleShippingUpdate = useCallback((methods: string[], options: ShippingOption[]) => {
    // Update the separate state variables
    onFormChangeAction("selectedShippingMethods", methods)
    onFormChangeAction("customShippingOptions", options)
    
    // Also update formData.shipping_config with the proper format
    const shippingConfigArray = options
      .filter((option) => methods.includes(option.id))
      .map((option) => ({
        shipping_method_id: option.id,
        label: option.name,
        price: option.price,
        allow_combined: option.allowCombined || false,
        combined_fee: option.combinedFee || "0",
      }))
    
    onFormChangeAction("shipping_config", shippingConfigArray)
  }, [onFormChangeAction])

  const isFormValid = () => {
    if (isPreorderLimitedEditMode) {
      // `quantity` here is the remaining ("X left") stock, not a total — it may
      // be anything >= 0 (0 closes the listing to new orders), but never more
      // than the capacity headroom (max_orders − already sold) when a cap is set.
      const quantity = Number(formData.quantity ?? 0)
      if (!(computedSelectedMethods.length > 0 && Number.isInteger(quantity) && quantity >= 0)) {
        return false
      }
      const max = formData.max_orders
      if (max != null && quantity > Math.max(0, Number(max) - preorderLock.paidCount)) {
        return false
      }
      return true
    }
    const formDataCount = formData.images?.length ?? 0
    let editorCount = 0
    let finalPhotoCount = formDataCount // Default to formData count
    
    if (detailsTabRef.current) {
      try {
        const currentPhotos = detailsTabRef.current.getCurrentPhotosInOrder()
        editorCount = currentPhotos.length
        
        finalPhotoCount = Math.max(formDataCount, editorCount)
        
      } catch (error) {
        // Fallback to formData count only
        finalPhotoCount = formDataCount
      }
    } else {
      finalPhotoCount = formDataCount
    }
    
    // 🎯 ADDITIONAL SAFETY: Never let photo count be 0 if formData has images
    // BUT: If both are 0, then we truly have 0 photos (allow this case)
    if (finalPhotoCount === 0 && formDataCount > 0) {
      finalPhotoCount = formDataCount
    }
    
    // 🎯 EDGE CASE: If both sources say 0, we truly have 0 photos
    if (formDataCount === 0 && editorCount === 0) {
      finalPhotoCount = 0
    }
    
    const isAuction = (formData as any).listing_format === "auction"
    const isOffersOnly = (formData as any).listing_format === "offers_only"
    const hasPricing = isOffersOnly
      ? true
      : isAuction
        ? Number((formData as any).auction_start_price) > 0
        : Number(formData.price) > 0

    const isValid = (
      formData.title.trim() !== "" &&
      hasPricing &&
      selectedShippingMethods.length > 0 &&
      finalPhotoCount > 0
    )
    
    return isValid
  }

  const handleSaveEdit = async () => {
    if (!listing) return

    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: profile } = await supabase.from("profiles").select("banned_at").eq("id", user.id).maybeSingle();
      if ((profile as { banned_at?: string | null } | null)?.banned_at) {
        toast({
          title: "Error",
          description: "Your account has been suspended. You cannot edit listings.",
          variant: "destructive",
        });
        return;
      }
    }

    setIsLoading(true)
    try {
      const shippingConfigToSave = formData.shipping_config || []

      if (isPreorderListing && preorderLock.locked && !preorderLock.loading && !isAdminContext) {
        // The production threshold (min_orders) is only editable while the
        // pre-order is still "open"; once funded the API rejects it.
        const canEditMinOrders = preorderLock.status === "open"
        const intent: PatchListingRequest = {
          shipping_config: shippingConfigToSave,
          quantity: formData.quantity,
          max_orders: formData.max_orders ?? null,
          ...(canEditMinOrders ? { min_orders: preorderFields.min_orders ?? null } : {}),
        }
        try {
          await updateListing({ listingId: listing.id, intent })
        } catch (err) {
          const description = err instanceof UpdateListingError
            ? (err.serverError ?? "Failed to update listing. Please try again.")
            : "Failed to update listing. Please try again."
          toast({
            title: "Error",
            description,
            variant: "destructive",
          })
          return
        }
        const updatedListingData: Listing = {
          ...listing!,
          shipping_config: shippingConfigToSave,
          quantity: formData.quantity,
          max_orders: formData.max_orders ?? null,
          ...(canEditMinOrders ? { min_orders: preorderFields.min_orders ?? null } : {}),
        }
        onSaveAction(updatedListingData)
        onOpenChangeAction(false)
        return
      }

      let photosToSave = formData.images || []
      
      if (detailsTabRef.current) {
        const currentPhotosInOrder = detailsTabRef.current.getCurrentPhotosInOrder()
        if (currentPhotosInOrder.length > 0) {
          photosToSave = currentPhotosInOrder
          
          // Also update formData for consistency
          onFormChangeAction("images", currentPhotosInOrder)
        }
      }

      // Upload images and update with public URLs.
      const { updatedPhotos, publicUrls } = await uploadImages(photosToSave, formData.title)
      onFormChangeAction("images", updatedPhotos)

      const updatedListing: Listing = {
        ...listing,
        title: formData.title,
        description: formData.description,
        price: Number(formData.price),
        shipping_config: shippingConfigToSave,
        grade: formData.grade,
        condition: formData.condition,
        quantity: formData.quantity,
        // `ListingRow.image_urls` is deliberately NOT branded (400+ read sites,
        // see the note on its declaration in `types/listing.ts`), so un-brand
        // here rather than resolve — these are the values just persisted.
        image_urls: rawImageRefValues(publicUrls),
        brand_id: formData.brand_id ?? null,
        theme_id: formData.theme_id ?? null,
        product_type_id: formData.product_type_id ?? null,
        theme_custom_text: formData.theme_custom_text ?? null,
        brand_custom_text: formData.brand_custom_text ?? null,
        product_type_custom_text: formData.product_type_custom_text ?? null,
        scheduled_publish_at: formData.scheduled_publish_at ?? null,
        cardCategory: formData.listingCategory,
        cardDraft: formData.cardDraft,
        // Phase 3 (2026-06-29): `accepts_offers` collapsed into
        // `listing_format`. Consumers derive via `isOfferEnabledFormat()`
        // from `@kraahl/contract`.
        listing_format: formData.listing_format ?? listing.listing_format,
        auto_decline_below: formData.auto_decline_below ?? (listing as { auto_decline_below?: number | null }).auto_decline_below ?? null,
        auction_start_price: (listing as { auction_start_price?: number | null }).auction_start_price ?? null,
      }

      // Si on est dans un contexte admin, utiliser la route API admin pour contourner les RLS
      if (isAdminContext) {
        const response = await fetch(`/api/admin/listings/${listing.id}/update`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            title: updatedListing.title,
            description: updatedListing.description,
            price: updatedListing.price,
            shipping_config: updatedListing.shipping_config,
            grade: updatedListing.grade,
            condition: updatedListing.condition,
            quantity: updatedListing.quantity,
            image_urls: publicUrls,
            brand_id: updatedListing.brand_id ?? null,
            theme_id: updatedListing.theme_id ?? null,
            product_type_id: updatedListing.product_type_id ?? null,
            theme_custom_text: updatedListing.theme_custom_text ?? null,
            brand_custom_text: updatedListing.brand_custom_text ?? null,
            product_type_custom_text: updatedListing.product_type_custom_text ?? null,
            scheduled_publish_at: updatedListing.scheduled_publish_at ?? null,
            cardCategory: formData.listingCategory,
            cardDraft: formData.cardDraft,
            // Phase 3 (2026-06-29): `accepts_offers` collapsed into
            // `listing_format` — admin route reads format only.
            listing_format: formData.listing_format ?? listing.listing_format,
            auto_decline_below: formData.auto_decline_below ?? null,
          }),
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          console.error("Error updating listing via admin API:", errorData)
          toast({
            title: "Error",
            description: errorData.error || "Failed to update listing. Please try again.",
            variant: "destructive",
          })
          return
        }

        const result = await response.json()
        if (result.listing) {
          updatedListing.id = result.listing.id
          updatedListing.created_at = result.listing.created_at
        }
      } else {
        // Build a PatchListingRequest intent. The schema is a union on
        // `cardCategory`: `"trading_card"` (card branch) vs `"lego" | null |
        // undefined` (lego branch — taxonomy fields permitted only here).
        // `ListingRow.shipping_config` is wire-loose (`string | unknown[]`)
        // — legacy stringified form exists in old rows. Producer always
        // emits an array (see `shippingConfigToSave` above); normalise to
        // satisfy `PatchListingRequest.shipping_config: unknown[]`.
        const shippingConfigArr = Array.isArray(updatedListing.shipping_config)
          ? updatedListing.shipping_config
          : []
        // Domain `ListingRow.grade` is `string | number`; producer historically
        // emitted whatever it had (route reads either). Schema pins string —
        // coerce at the boundary, mirroring the pre-builder fetch body which
        // serialised numbers as JSON numbers (route accepts both).
        // WS3.2.b — when the seller flipped to (or stayed on) `live_rate`,
        // ship the 8 shippo keys; the contract `superRefine` enforces all
        // four parcel dims + carrier_provider as a single coherence block.
        // Producer always emits `carrier_provider: 'shippo'` on live_rate —
        // the only carrier today (MVP). Legacy mode wipes the parcel.
        const isLiveRate = formData.shippingMode === "live_rate"
        const shippoFields: Partial<PatchListingRequest> =
          formData.shippingMode !== undefined
            ? {
                shipping_mode: formData.shippingMode,
                carrier_provider: isLiveRate ? "shippo" : null,
                // Canonical-only (slice C2): grams + mm direct from the form.
                parcel_weight_grams: isLiveRate ? formData.parcel?.weight ?? null : null,
                parcel_length_mm: isLiveRate ? formData.parcel?.length ?? null : null,
                parcel_width_mm: isLiveRate ? formData.parcel?.width ?? null : null,
                parcel_height_mm: isLiveRate ? formData.parcel?.height ?? null : null,
              }
            : {}
        // Per-listing ship-from override (ADR_LISTING_SHIP_FROM_ADDRESS.md
        // Phase 1). Tri-state: form key absent → omit from PATCH (server
        // leaves column untouched); explicit `null` → clear pin; uuid →
        // set pin. `formData.ship_from_address_id === undefined` means the
        // seller never touched the picker in this edit session.
        const shipFromFields: Partial<PatchListingRequest> =
          formData.ship_from_address_id !== undefined
            ? { ship_from_address_id: formData.ship_from_address_id }
            : {}
        const commonFields = {
          title: updatedListing.title,
          description: updatedListing.description,
          shipping_config: shippingConfigArr,
          grade: updatedListing.grade != null ? String(updatedListing.grade) : undefined,
          condition: updatedListing.condition,
          quantity: updatedListing.quantity,
          image_urls: publicUrls,
          scheduled_publish_at: updatedListing.scheduled_publish_at ?? null,
          // cardDraft is wire-permissive (Record<string, unknown>); the
          // CardListingDraft domain interface lacks an index signature, so
          // cast at the boundary. The route reads ~25 optional fields off it.
          cardDraft: formData.cardDraft as Record<string, unknown> | undefined,
          ...shippoFields,
          ...shipFromFields,
        }
        const preorderOrCommerce: Partial<PatchListingRequest> = isPreorderListing
          ? (!preorderLock.locked
            ? {
                preorder_deadline: preorderFields.preorder_deadline,
                estimated_delivery: preorderFields.estimated_delivery,
                min_orders: preorderFields.min_orders,
                max_orders: preorderFields.max_orders,
              }
            : {})
          : {
              // Phase 3 (2026-06-29): `accepts_offers` collapsed into
              // `listing_format` — derive via `isOfferEnabledFormat()`.
              price: updatedListing.price,
              listing_format: (formData.listing_format ?? listing.listing_format) as PatchListingRequest["listing_format"],
              auto_decline_below: formData.auto_decline_below ?? null,
            }
        const intent: PatchListingRequest = (() => {
          switch (formData.listingCategory) {
            case "trading_card":
              return {
                ...commonFields,
                ...preorderOrCommerce,
                comicDraft: undefined,
                cardCategory: "trading_card" as const,
              }
            case "comic":
              return {
                ...commonFields,
                ...preorderOrCommerce,
                cardDraft: undefined,
                cardCategory: "comic" as const,
                comicDraft: formData.comicDraft,
              }
            case "lego":
            case undefined:
              return {
                ...commonFields,
                ...preorderOrCommerce,
                comicDraft: undefined,
                cardCategory: (formData.listingCategory ?? null) as "lego" | null,
                brand_id: updatedListing.brand_id ?? null,
                theme_id: updatedListing.theme_id ?? null,
                product_type_id: updatedListing.product_type_id ?? null,
                theme_custom_text: updatedListing.theme_custom_text ?? null,
                brand_custom_text: updatedListing.brand_custom_text ?? null,
                product_type_custom_text: updatedListing.product_type_custom_text ?? null,
              }
            default:
              return unreachableCategory(formData.listingCategory)
          }
        })()

        try {
          await updateListing({ listingId: listing.id, intent })
        } catch (err) {
          const description = err instanceof UpdateListingError
            ? (err.serverError ?? "Failed to update listing. Please try again.")
            : "Failed to update listing. Please try again."
          toast({
            title: "Error",
            description,
            variant: "destructive",
          })
          return
        }
      }

      // OPTIMISTIC UPDATE: Call onSaveAction immediately with updated data
      // This allows instant UI feedback without waiting for server refetch
      const updatedListingData: Listing = {
        ...listing!,
        title: formData.title,
        description: formData.description,
        price: formData.price,
        image_urls: rawImageRefValues(publicUrls),
        shipping_config: formData.shipping_config,
        grade: formData.grade,
        condition: formData.condition,
        quantity: formData.quantity,
        brand_id: formData.brand_id ?? null,
        theme_id: formData.theme_id ?? null,
        product_type_id: formData.product_type_id ?? null,
        theme_custom_text: formData.theme_custom_text ?? null,
        brand_custom_text: formData.brand_custom_text ?? null,
        product_type_custom_text: formData.product_type_custom_text ?? null,
        scheduled_publish_at: formData.scheduled_publish_at ?? null,
        cardCategory: formData.listingCategory,
        cardDraft: formData.cardDraft,
        // Phase 3 (2026-06-29): `accepts_offers` collapsed into
        // `listing_format`.
        listing_format: formData.listing_format ?? listing!.listing_format,
        auto_decline_below: formData.auto_decline_below ?? (listing as { auto_decline_below?: number | null }).auto_decline_below ?? null,
        auction_start_price: (listing as { auction_start_price?: number | null }).auction_start_price ?? null,
      }
      
      onSaveAction(updatedListingData)
      
      onOpenChangeAction(false)
    } catch (err) {
      console.error("[edit-listing] save failed", err)
      toast({
        title: "Error",
        description: err instanceof TypeError
          ? "Network error — please check your connection and try again."
          : "Failed to update listing. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (!loadingProp && !listing) return null

  const detailsSkeleton = (
    <div className="grid gap-6">
      <div className="space-y-4 pt-2">
        <div className="flex items-center justify-between">
          <Skeleton className="h-5 w-28 bg-sheet" />
          <Skeleton className="h-4 w-10 bg-sheet" />
        </div>
        <Skeleton className="h-[140px] w-full rounded-2xl bg-sheet" />
      </div>
      <div className="grid gap-4">
        <div className="space-y-4">
          <Skeleton className="h-6 w-20 bg-sheet" />
          <Skeleton className="h-[120px] w-full rounded-2xl bg-sheet" />
        </div>
        <div>
          <Skeleton className="h-14 w-full rounded-full bg-sheet" />
          <Skeleton className="mt-1 h-3 w-12 ml-auto bg-sheet" />
        </div>
        <div>
          <Skeleton className="h-[120px] w-full rounded-lg bg-sheet" />
          <Skeleton className="mt-1 h-3 w-12 ml-auto bg-sheet" />
        </div>
        <div className="bg-sheet/50 p-4 rounded-lg space-y-2">
          <Skeleton className="h-4 w-12 bg-sheet" />
          <Skeleton className="h-12 w-full bg-sheet" />
        </div>
      </div>
    </div>
  )

  const showListingTabs = Boolean(listing) && !preorderEditLoading && !isPreorderLimitedEditMode

  const preorderEditor = isPreorderListing && !preorderLock.locked && !preorderLock.loading && listing ? (
    <div className="mb-4">
      <PreorderEditSection
        listingId={listing.id}
        initial={{
          preorder_deadline: (listing as any).preorder_deadline ?? null,
          estimated_delivery: (listing as any).estimated_delivery ?? null,
          min_orders: (listing as any).min_orders ?? null,
          max_orders: (listing as any).max_orders ?? null,
        }}
        onChange={handlePreorderChange}
      />
    </div>
  ) : null

  const preorderLimitedPanel = listing ? (
    <div className="space-y-4">
      <div className="rounded-2xl border border-sheet-handle/80 bg-sheet/60 p-4 text-sm text-foreground/80">
        {tPreorderEdit("shippingOnlyHint")}
      </div>
      <div className="rounded-2xl border border-sheet-handle/80 bg-sheet/60 p-4 space-y-4">
        <div>
          <Label className="text-sm font-medium text-foreground">{tPreorderEdit("quantityAvailableLabel")}</Label>
          <NumericInput
            codec={countCodec}
            value={formData.quantity ?? undefined}
            onValueChange={(next) => onFormChangeAction("quantity", next ?? 0)}
            className="mt-2 h-12 rounded-3xl bg-muted text-base text-foreground border-0"
          />
          <p className="mt-2 text-xs text-muted-foreground/70">
            {tPreorderEdit("quantityTotalHint")}
          </p>
          {preorderLock.paidCount > 0 ? (
            <p className="mt-1 text-xs text-muted-foreground">
              {tPreorderEdit("quantityMinHint", { count: preorderLock.paidCount })}
            </p>
          ) : null}
          {(() => {
            const max = formData.max_orders
            if (max == null) return null
            const remainingCapacity = Math.max(0, Number(max) - preorderLock.paidCount)
            const q = Number(formData.quantity ?? 0)
            if (q <= remainingCapacity) return null
            return (
              <p className="mt-1 text-xs text-red-400">
                {tPreorderEdit("quantityCapacityError", {
                  n: remainingCapacity,
                  max: Number(max),
                  sold: preorderLock.paidCount,
                })}
              </p>
            )
          })()}
        </div>
        {preorderLock.status === "open" ? (
          <div>
            <Label className="text-sm font-medium text-foreground">{tPreorderEdit("minOrders")}</Label>
            <NumericInput
              codec={countCodec}
              value={preorderFields.min_orders ?? undefined}
              placeholder={tPreorderEdit("minOrdersPlaceholder")}
              onValueChange={(value) => {
                // `null` is this form's "unset" — the column is nullable and
                // the placeholder shows when it is. Previously a below-minimum
                // entry was rejected with an early `return`, which left the
                // input showing a digit the form did not have; the value is
                // now stored and the server-side floor reports it.
                const next = value ?? null
                onFormChangeAction("min_orders", next)
                setPreorderFields((prev) => ({ ...prev, min_orders: next }))
              }}
              className="mt-2 h-12 rounded-3xl bg-muted text-base text-foreground border-0"
            />
            <p className="mt-2 text-xs text-muted-foreground/70">
              {tPreorderEdit("minOrdersLockedHint", { count: preorderLock.paidCount })}
            </p>
          </div>
        ) : null}
        <div>
          <Label className="text-sm font-medium text-foreground">{tPreorderEdit("maxOrders")}</Label>
          <NumericInput
            codec={countCodec}
            value={formData.max_orders ?? undefined}
            placeholder={tPreorderEdit("maxOrdersPlaceholder")}
            onValueChange={(value) => {
              const next = value ?? null
              onFormChangeAction("max_orders", next)
              setPreorderFields((prev) => ({ ...prev, max_orders: next }))
            }}
            className="mt-2 h-12 rounded-3xl bg-muted text-base text-foreground border-0"
          />
        </div>
      </div>
      {shippingOriginBinding ? (
        <EditShippingTab
          initialSelectedMethods={computedSelectedMethods}
          initialCustomOptions={computedCustomOptions}
          onUpdateAction={handleShippingUpdate}
          origin={shippingOriginBinding}
          currencyCode={currencyCode}
          compact={!isDesktop}
          shippingMode={shippingModeValue}
          onShippingModeChange={handleShippingModeChange}
          shippingModeLocked={shippingModeLocked}
          shippingModeLockedReason={tML("shippingModeLockedReason", { default: "Cannot change shipping mode — orders are in-flight." })}
          sellerUserId={isAdminContext ? sellerUserId : undefined}
        />
      ) : (
        <Skeleton className="h-72 w-full rounded-2xl bg-muted/40" />
      )}
    </div>
  ) : null

  const tabsContent = !listing ? null : preorderEditLoading ? (
    detailsSkeleton
  ) : isPreorderLimitedEditMode ? (
    preorderLimitedPanel
  ) : (
    <>
      <TabsContent value="details" className={!isDesktop ? "mt-0 pt-3" : ""}>
        {preorderEditor}
        <DetailsTab
          ref={detailsTabRef}
          listing={{
            ...listing,
            images: formData.images || [],
          }}
          formData={{ ...formData, images: formData.images || [] }}
          setFormDataAction={(data) => {
            Object.keys(data).forEach(key => {
              onFormChangeAction(key, data[key])
            })
          }}
          onFormChangeAction={onFormChangeAction}
          onImagesChangeAction={(imgs) => onFormChangeAction("images", imgs)}
          hideQuantity={hideQuantity}
          isEditing={true}
        />
      </TabsContent>

      <TabsContent value="condition" className={!isDesktop ? "mt-0 pt-2" : ""}>
        <ConditionTab
          formData={formData}
          onFormChange={onFormChangeAction}
          needsGrading={effectiveNeedsGrading}
        />
      </TabsContent>

      <TabsContent value="shipping" className={!isDesktop ? "mt-0 pt-2" : ""}>
        {shippingOriginBinding ? (
          <EditShippingTab
            initialSelectedMethods={computedSelectedMethods}
            initialCustomOptions={computedCustomOptions}
            onUpdateAction={handleShippingUpdate}
            origin={shippingOriginBinding}
            currencyCode={currencyCode}
            compact={!isDesktop}
            shippingMode={shippingModeValue}
            onShippingModeChange={handleShippingModeChange}
            shippingModeLocked={shippingModeLocked}
            shippingModeLockedReason={tML("shippingModeLockedReason", { default: "Cannot change shipping mode — orders are in-flight." })}
            sellerUserId={isAdminContext ? sellerUserId : undefined}
          />
        ) : (
          <Skeleton className="h-72 w-full rounded-2xl bg-muted/40" />
        )}
      </TabsContent>
    </>
  )

  const sheetZ = elevatedForChat ? SHEET_Z_GLOBAL : SHEET_Z_DEFAULT

  const saveDisabled =
    loadingProp ||
    isLoading ||
    preorderEditLoading ||
    (isPreorderLimitedEditMode
      ? !isFormValid() || (disableSaveWhenUnchanged && !hasFormChanges)
      : !isFormValid() ||
        (disableSaveWhenUnchanged && !hasFormChanges && !preorderDirty) ||
        (isPreorderListing && !preorderFields.isValid))

  const mobileSaveFooter = (
    <Button
      onClick={handleSaveEdit}
      disabled={saveDisabled}
      className={creationFlowPrimaryCtaClass}
    >
      {isLoading ? (
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-background border-t-foreground" />
          {tML("savingChanges")}
        </div>
      ) : (
        tML("saveChanges")
      )}
    </Button>
  )

  if (!isDesktop) {
    return (
      <Tabs value={activeTab} onChange={setActiveTab} className="contents">
        <DockSheet
          open={open}
          onOpenChange={onOpenChangeAction}
          title={tML("editListing")}
          zIndex={sheetZ}
          belowTitle={
            showListingTabs ? (
              <TabsList className="flex gap-1 p-1 rounded-2xl bg-muted border-0 mb-0">
                <TabsTrigger value="details" className="flex-1 rounded-xl py-2.5 border-0 text-muted-foreground hover:text-foreground hover:bg-sheet-handle/50 data-[state=active]:bg-sheet-handle data-[state=active]:text-foreground data-[state=active]:hover:bg-sheet-handle">{tML("tabDetails")}</TabsTrigger>
                <TabsTrigger value="condition" className="flex-1 rounded-xl py-2.5 border-0 text-muted-foreground hover:text-foreground hover:bg-sheet-handle/50 data-[state=active]:bg-sheet-handle data-[state=active]:text-foreground data-[state=active]:hover:bg-sheet-handle">{tML("tabCondition")}</TabsTrigger>
                <TabsTrigger value="shipping" className="flex-1 rounded-xl py-2.5 border-0 text-muted-foreground hover:text-foreground hover:bg-sheet-handle/50 data-[state=active]:bg-sheet-handle data-[state=active]:text-foreground data-[state=active]:hover:bg-sheet-handle">{tML("tabShipping")}</TabsTrigger>
              </TabsList>
            ) : undefined
          }
          footer={mobileSaveFooter}
        >
          <div className="text-foreground overflow-x-hidden">
            {loadingProp ? detailsSkeleton : tabsContent}
          </div>
        </DockSheet>
      </Tabs>
    )
  }

  if (isDesktop) {
    const desktopFooter = (
      <Button
        onClick={handleSaveEdit}
        disabled={saveDisabled}
        className={creationFlowPrimaryCtaClass}
      >
        {isLoading ? (
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-background border-t-foreground" />
            {tML("savingChanges")}
          </div>
        ) : (
          tML("saveChanges")
        )}
      </Button>
    )
    return (
      // `Tabs` wraps the CARD, not its body: `belowTitle` renders inside the
      // primitive's chrome, so the `TabsList` would sit outside the provider
      // if the context started below it. Same shape as the mobile branch.
      <Tabs value={activeTab} onChange={setActiveTab} className="contents">
        <DialogCard
          open={open}
          onOpenChange={onOpenChangeAction}
          nested={elevatedForChat}
          title={tML("editListing")}
          belowTitle={
            <div className="space-y-3">
              <DialogDescription className="text-center text-muted-foreground">{tML("editSubtitle")}</DialogDescription>
              {showListingTabs && (
                <TabsList className="flex gap-1 p-1 rounded-2xl bg-muted border-0 w-full">
                  <TabsTrigger value="details" className="flex-1 rounded-xl py-2.5 border-0 text-muted-foreground hover:text-foreground hover:bg-sheet-handle/50 data-[state=active]:bg-sheet-handle data-[state=active]:text-foreground">{tML("tabDetails")}</TabsTrigger>
                  <TabsTrigger value="condition" className="flex-1 rounded-xl py-2.5 border-0 text-muted-foreground hover:text-foreground hover:bg-sheet-handle/50 data-[state=active]:bg-sheet-handle data-[state=active]:text-foreground">{tML("tabCondition")}</TabsTrigger>
                  <TabsTrigger value="shipping" className="flex-1 rounded-xl py-2.5 border-0 text-muted-foreground hover:text-foreground hover:bg-sheet-handle/50 data-[state=active]:bg-sheet-handle data-[state=active]:text-foreground">{tML("tabShipping")}</TabsTrigger>
                </TabsList>
              )}
            </div>
          }
          panelClassName="max-w-2xl h-[80dvh]"
          footer={desktopFooter}
        >
          {loadingProp ? detailsSkeleton : tabsContent}
        </DialogCard>
      </Tabs>
    )
  }

  // Fallback (ne devrait jamais arriver)
  return null
}
